import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, ClipboardList, MoreVertical, Pencil, Trash2, ArrowUpRight, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import { useOrders, useDeleteOrder } from '@/hooks/useSales';
import {
  ORDER_STATUS_LABELS, ORDER_STATUSES, ACTIVE_ORDER_STATUSES,
  type Order, type OrderStatus,
} from '@/types/sales';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card, SectionHeader } from '@/components/ui/Card';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { OrderStatusBadge } from '@/components/quotes/StatusBadges';
import { OrderFormModal } from '@/components/orders/OrderFormModal';
import { formatCurrency } from '@/lib/utils';

export function OrdersListPage() {
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'orders', 'write') : false;

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'' | OrderStatus>('');

  const { data, isLoading, error } = useOrders();
  const deleteOrder = useDeleteOrder();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Order | null>(null);
  const [deleting, setDeleting] = useState<Order | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((o) => {
      if (status && o.status !== status) return false;
      if (search.trim()) {
        const s = search.toLowerCase();
        const hay = `${o.number} ${o.clientName ?? ''} ${o.projectName ?? ''} ${o.poNumber ?? ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [data, search, status]);

  const stats = useMemo(() => {
    if (!data) return { total: 0, active: 0, value: 0, lateCount: 0 };
    const now = Date.now();
    return {
      total: data.length,
      active: data.filter((o) => ACTIVE_ORDER_STATUSES.includes(o.status)).length,
      value: data
        .filter((o) => ACTIVE_ORDER_STATUSES.includes(o.status))
        .reduce((s, o) => s + (o.totals?.total ?? 0), 0),
      lateCount: data.filter((o) =>
        ACTIVE_ORDER_STATUSES.includes(o.status) &&
        o.promisedDate && new Date(o.promisedDate).getTime() < now &&
        !['shipped', 'delivered'].includes(o.status),
      ).length,
    };
  }, [data]);

  if (isLoading) return <LoaderScreen />;
  if (error) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load orders"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  async function onConfirmDelete() {
    if (!deleting) return;
    try {
      await deleteOrder.mutateAsync(deleting.id);
      toast.success(`Deleted ${deleting.number}`);
      setDeleting(null);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Delete failed.');
    }
  }

  return (
    <div className="space-y-8">
      <SectionHeader
        kicker={`Operations · ${data?.length ?? 0} orders`}
        title="Work Orders & Pipeline."
        description="Every order from receipt through delivery. Status milestones flow live to the production floor."
        action={canWrite && (
          <Button leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
            New Order
          </Button>
        )}
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <Stat label="Total"        value={String(stats.total)} />
        <Stat label="Active"       value={String(stats.active)} />
        <Stat label="Past Due"     value={String(stats.lateCount)} alert={stats.lateCount > 0} />
        <Stat label="Active Value" value={stats.value ? formatCurrency(stats.value) : '—'} accent />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[16rem]">
          <Input
            placeholder="Search by number, client, project, PO…"
            leftIcon={<Search size={14} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-44">
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as OrderStatus | '')}
            options={[
              { value: '', label: 'All statuses' },
              ...ORDER_STATUSES.map((s) => ({ value: s, label: ORDER_STATUS_LABELS[s] })),
            ]}
          />
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon={ClipboardList}
            title={search || status ? 'No orders match your filters' : 'No orders yet'}
            description={search || status ? 'Try clearing some filters.' : 'Create your first work order to get started.'}
            action={canWrite && !search && !status && (
              <Button leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
                New Order
              </Button>
            )}
          />
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="kicker px-6 py-3 font-normal">Order</th>
                <th className="kicker px-6 py-3 font-normal">Client / Project</th>
                <th className="kicker px-6 py-3 font-normal">Status</th>
                <th className="kicker px-6 py-3 font-normal">Promised</th>
                <th className="kicker px-6 py-3 font-normal text-right">Total</th>
                <th className="kicker px-6 py-3 font-normal w-12" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => {
                const isLate = o.promisedDate && new Date(o.promisedDate).getTime() < Date.now()
                  && ACTIVE_ORDER_STATUSES.includes(o.status);
                return (
                  <tr key={o.id} className="border-b border-border last:border-0 hover:bg-bg-hover/40 transition-colors group">
                    <td className="px-6 py-4">
                      <Link to={`/orders/${o.id}`} className="font-mono text-brass-300 hover:text-brass-200">
                        {o.number}
                      </Link>
                      {o.poNumber && (
                        <div className="text-2xs text-text-dim font-mono mt-0.5">PO {o.poNumber}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-text">{o.clientName ?? <span className="text-text-dim">—</span>}</div>
                      {o.projectName && <div className="text-xs text-text-dim truncate max-w-xs">{o.projectName}</div>}
                    </td>
                    <td className="px-6 py-4"><OrderStatusBadge status={o.status} /></td>
                    <td className={`px-6 py-4 tabular text-xs ${isLate ? 'text-danger' : 'text-text-muted'}`}>
                      {o.promisedDate ? new Date(o.promisedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                      {isLate && <span className="ml-2 kicker text-2xs text-danger">Past Due</span>}
                    </td>
                    <td className="px-6 py-4 text-right tabular text-text">
                      {o.totals?.total ? formatCurrency(o.totals.total) : '—'}
                    </td>
                    <td className="px-6 py-4 text-right relative">
                      {canWrite && (
                        <>
                          <button
                            onClick={(e) => { e.preventDefault(); setOpenMenu(openMenu === o.id ? null : o.id); }}
                            className="p-1.5 text-text-dim hover:text-text hover:bg-bg-hover rounded-sm transition-colors opacity-0 group-hover:opacity-100"
                            aria-label="Actions"
                          >
                            <MoreVertical size={15} />
                          </button>
                          {openMenu === o.id && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                              <div className="absolute right-4 top-12 z-20 w-44 bg-bg-raised border border-border-strong rounded-sm shadow-raised py-1 animate-fade-in">
                                <Link to={`/orders/${o.id}`} className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text hover:bg-bg-hover">
                                  <ArrowUpRight size={13} /> Open
                                </Link>
                                <button onClick={() => { setEditing(o); setOpenMenu(null); }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text hover:bg-bg-hover text-left">
                                  <Pencil size={13} /> Edit
                                </button>
                                <div className="my-1 border-t border-border" />
                                <button onClick={() => { setDeleting(o); setOpenMenu(null); }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-danger hover:bg-danger/10 text-left">
                                  <Trash2 size={13} /> Delete
                                </button>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <OrderFormModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <OrderFormModal open={!!editing} onClose={() => setEditing(null)} order={editing} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={onConfirmDelete}
        loading={deleteOrder.isPending}
        destructive
        title={`Delete ${deleting?.number}?`}
        confirmLabel="Delete order"
        description="This permanently removes the order. Cannot be undone."
      />
    </div>
  );
}

function Stat({ label, value, accent, alert }: { label: string; value: string; accent?: boolean; alert?: boolean }) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-2">{label}</div>
      <div className={`font-display text-2xl tabular ${accent ? 'text-brass-300' : alert ? 'text-warning' : 'text-text'}`}>
        {value}
      </div>
    </div>
  );
}
